import { getEnvVars } from '@environment';
import { HubConnection, HubConnectionBuilder, LogLevel } from '@microsoft/signalr';
import { useAppSelector } from '@unistocks/shared/hooks/redux-hooks';
import { setSignalRState } from '@unistocks/shared/signalr/signalr-status';
import { selectToken } from '@unistocks/shared/store/root.slice';
import { useEffect, useMemo } from 'react';

const { production, apiUrl } = getEnvVars();

export const useSignalRHub = (hubEndpoint: string) => {
  const token = useAppSelector(selectToken);
  const connection = useMemo<HubConnection | null>(() => {
    if (!token?.access_token) return null;

    return new HubConnectionBuilder()
      .configureLogging(production ? LogLevel.None : LogLevel.Information)
      .withUrl(`${apiUrl}/signalr-hubs/${hubEndpoint}`, {
        accessTokenFactory: () => token?.access_token ?? '',
        withCredentials: production
      })
      .withAutomaticReconnect()
      .build();
  }, [hubEndpoint, token?.access_token]);

  useEffect(() => {
    if (!connection) return;

    const syncState = () => {
      setSignalRState(hubEndpoint, connection.state);
    };

    syncState();
    connection.onreconnecting(syncState);
    connection.onreconnected(syncState);
    connection.onclose(syncState);

    connection.start().finally(syncState);

    return () => {
      connection.stop().finally(syncState);
    };
  }, [connection, hubEndpoint]);

  return connection;
};
